import dayjs from 'dayjs';

const DEFAULT_LOCALE = 'es-ES';
const DEFAULT_CURRENCY = 'EUR';

// es-ES no agrupa los miles en cifras de 4 digitos ("4952,42 €") y si en las de
// 5 ("12.021,71 €"): en una misma fila de KPI parecia un error. Siempre agrupado.
const GROUPING = { useGrouping: 'always' } as const;

/** Dato ausente: null, NaN o infinito (p. ej. una división por cero). Se pinta `—`.
 * El guard solo estrecha a `number` en la rama falsa, que es la que se usa. */
function isMissing(value: number | null | undefined): value is null | undefined {
  return value === null || value === undefined || !Number.isFinite(value);
}

export function formatCurrency(
  value: number | null | undefined,
  currency = DEFAULT_CURRENCY,
  locale = DEFAULT_LOCALE,
): string {
  if (isMissing(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    ...GROUPING,
  }).format(value);
}

/**
 * Importe redondeado a la unidad: `2000` → `2.000 €`. Para ejes de gráficos y
 * cifras grandes donde los céntimos solo añaden ruido.
 */
export function formatCurrencyRounded(
  value: number | null | undefined,
  currency = DEFAULT_CURRENCY,
  locale = DEFAULT_LOCALE,
): string {
  if (isMissing(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...GROUPING,
  }).format(value);
}

/** Convierte un código ISO 4217 ("EUR") en su símbolo ("€") para addons de
 * inputs y etiquetas cortas. Si ya es un símbolo (o el código es desconocido)
 * lo devuelve tal cual. */
export function currencySymbol(code: string | null | undefined, locale = DEFAULT_LOCALE): string {
  if (!code) return '€';
  if (code.length !== 3) return code;
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

export function formatNumber(
  value: number | null | undefined,
  opts: Intl.NumberFormatOptions = {},
  locale = DEFAULT_LOCALE,
): string {
  if (isMissing(value)) return '—';
  return new Intl.NumberFormat(locale, { ...GROUPING, ...opts }).format(value);
}

export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1,
  locale = DEFAULT_LOCALE,
): string {
  if (isMissing(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    ...GROUPING,
  }).format(value);
}

/** Entero con separador de miles: `formatInteger(12345.6)` → `12.346`. Para recuentos. */
export function formatInteger(value: number | null | undefined): string {
  return formatNumber(value, { maximumFractionDigits: 0 });
}

/** Número con decimales fijos y coma decimal: `formatFixed(1.6, 2)` → `1,60`. */
export function formatFixed(value: number | null | undefined, digits: number): string {
  return formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Ratio multiplicador: `1,19×`. Para LTGP:CAC, POAS y similares. */
export function formatRatio(value: number | null | undefined, digits = 2): string {
  if (isMissing(value)) return '—';
  return `${formatFixed(value, digits)}×`;
}

/**
 * Valor que YA está en porcentaje, sin signo: `12.5` → `12,5 %`.
 *
 * `formatPercent` recibe una fracción (0.125); este es para métricas que el
 * backend ya entrega multiplicadas por 100 (CTR, tasas de conversión, márgenes).
 *
 * Decimales fijos, como `formatPercent`: con un máximo en vez de un fijo, la
 * misma columna mezclaba `95 %` con `96,3 %`. Para porcentajes enteros por
 * naturaleza (progreso, un descuento del 20 %) se pasa `0`.
 */
export function formatPercentValue(value: number | null | undefined, fractionDigits = 1): string {
  if (isMissing(value)) return '—';
  return `${formatNumber(value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} %`;
}

/**
 * Variación con signo sobre un valor que YA está en porcentaje (231.8 → `+231,8 %`).
 *
 * Para deltas de KPI y comparaciones contra referencia. Coma decimal y espacio
 * antes del `%`, como el resto de porcentajes de la app.
 */
export function formatSignedPercent(
  value: number | null | undefined,
  maxFractionDigits = 1,
): string {
  if (isMissing(value)) return '—';
  const abs = formatNumber(Math.abs(value), { maximumFractionDigits: maxFractionDigits });
  return `${value > 0 ? '+' : value < 0 ? '-' : ''}${abs} %`;
}

/** Variación con signo en puntos porcentuales: `+1,2 pts`. */
export function formatSignedPoints(
  value: number | null | undefined,
  maxFractionDigits = 1,
): string {
  if (isMissing(value)) return '—';
  const abs = formatNumber(Math.abs(value), { maximumFractionDigits: maxFractionDigits });
  return `${value > 0 ? '+' : value < 0 ? '-' : ''}${abs} pts`;
}

export function formatCompact(value: number | null | undefined, locale = DEFAULT_LOCALE): string {
  if (isMissing(value)) return '—';
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

/**
 * Duracion en lenguaje corto: `45 s`, `3 min`, `1 h 12 min`.
 *
 * Para tiempos transcurridos y ETAs de procesos en curso. Por debajo del
 * minuto se dan segundos, que es donde el usuario nota que algo se mueve; por
 * encima se redondea, porque `1 h 12 min 38 s` no ayuda a decidir nada.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total} s`;
  const minutos = Math.round(total / 60);
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto ? `${horas} h ${resto} min` : `${horas} h`;
}

export function formatDate(
  value: string | number | Date | null | undefined,
  pattern = 'D MMM YYYY',
): string {
  if (!value) return '—';
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  return d.format(pattern);
}

type DateInput = string | number | Date | null | undefined;

/**
 * Día local en `YYYY-MM-DD`, el formato que esperan la API y el store de fechas.
 *
 * Es el único conversor de fecha a día de la app. NO vale
 * `toISOString().slice(0, 10)`: pasa a UTC y en España, de medianoche a las
 * 01:00/02:00, devuelve el día anterior, así que el rango sale corrido un día.
 */
export function toIsoDate(value: Date): string;
export function toIsoDate(value: Date | null | undefined): string | undefined;
export function toIsoDate(value: Date | null | undefined): string | undefined {
  if (!value || Number.isNaN(value.getTime())) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/** `YYYY-MM-DD` (o un ISO con hora, de la que solo se lee el día) a medianoche local. */
export function parseIsoDate(value: string): Date {
  return new Date(`${value.slice(0, 10)}T00:00:00`);
}

/**
 * Fecha corta de periodo: `19 ago`, con año solo si no es el actual (`19 ago 2025`).
 *
 * Es el formato del selector de rango, así que cualquier fecha que describa un
 * periodo (referencia, ventana, rango de un informe) debe salir de aquí para
 * que la página no mezcle `19 ago`, `19 sept` y `2026-08-19`.
 */
export function formatShortDate(value: DateInput): string {
  if (!value) return '—';
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  return d.year() === dayjs().year() ? d.format('D MMM') : d.format('D MMM YYYY');
}

/**
 * Rango de fechas: `19 jul → 18 ago`. El año va al final si el rango no es del
 * año actual (`3 mar → 9 abr 2025`) y en ambos extremos si cruza de año
 * (`20 dic 2025 → 10 ene 2026`).
 */
export function formatDateRange(start: DateInput, end: DateInput): string {
  const a = dayjs(start ?? undefined);
  const b = dayjs(end ?? undefined);
  const okA = !!start && a.isValid();
  const okB = !!end && b.isValid();
  if (!okA && !okB) return '—';
  if (!okA) return `… → ${formatShortDate(end)}`;
  if (!okB) return `${formatShortDate(start)} → …`;
  // Un intervalo de un día (agrupación diaria) es una fecha, no "19 ago → 19 ago".
  if (a.isSame(b, 'day')) return formatShortDate(start);
  if (a.year() !== b.year()) return `${a.format('D MMM YYYY')} → ${b.format('D MMM YYYY')}`;
  if (a.year() === dayjs().year()) return `${a.format('D MMM')} → ${b.format('D MMM')}`;
  return `${a.format('D MMM')} → ${b.format('D MMM YYYY')}`;
}

export function formatDateTime(value: string | number | Date | null | undefined): string {
  return formatDate(value, 'D MMM YYYY · HH:mm');
}

export function formatRelative(value: string | number | Date | null | undefined): string {
  if (!value) return '—';
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  const diffMs = Date.now() - d.valueOf();
  const absSec = Math.round(Math.abs(diffMs) / 1000);
  const future = diffMs < 0;
  const thresholds: Array<[number, Intl.RelativeTimeFormatUnit, number]> = [
    [60, 'second', 1],
    [3600, 'minute', 60],
    [86400, 'hour', 3600],
    [2592000, 'day', 86400],
    [31536000, 'month', 2592000],
    [Infinity, 'year', 31536000],
  ];
  const fmt = new Intl.RelativeTimeFormat(DEFAULT_LOCALE, { numeric: 'auto' });
  for (const [max, unit, divisor] of thresholds) {
    if (absSec < max) {
      const amount = Math.round(absSec / divisor);
      return fmt.format(future ? amount : -amount, unit);
    }
  }
  return d.format('D MMM YYYY');
}

const ISO_IN_TEXT =
  /\b(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?\b/g;

/**
 * Fechas ISO dentro de un texto libre (títulos de Shopify, etiquetas que manda
 * el backend) en el formato de la app: `Meta · 2026-08-14 a 2026-09-12` →
 * `Meta · 14 ago a 12 sep`; `(2026-07-20 00:33)` → `(20 jul 2026 · 00:33)`.
 * La hora se respeta tal cual viene escrita, sin convertir zona horaria.
 */
export function formatDatesInText(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(ISO_IN_TEXT, (match, date: string, time?: string) => {
    const d = dayjs(time ? `${date}T${time}` : date);
    if (!d.isValid()) return match;
    return time ? formatDateTime(d.toDate()) : formatShortDate(d.toDate());
  });
}

/**
 * Sustantivo concordado con la cifra: `plural(1, 'campaña', 'campañas')` →
 * `1 campaña`; `plural(1225, 'unidad', 'unidades')` → `1.225 unidades`.
 *
 * La cifra sale con `formatInteger` (miles agrupados). Con `withCount: false`
 * solo devuelve la palabra, para cuando la cifra se pinta aparte.
 */
export function plural(
  n: number | null | undefined,
  singular: string,
  pluralForm: string,
  withCount = true,
): string {
  const word = n === 1 ? singular : pluralForm;
  return withCount ? `${formatInteger(n ?? 0)} ${word}` : word;
}

export function initials(nameOrEmail: string | null | undefined): string {
  if (!nameOrEmail) return '?';
  return nameOrEmail
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');
}
